import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UploadService, UploadResponse } from '../../core/services/upload.service';

@Component({
  selector: 'app-image-uploader',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="upload-container">
      <div 
        class="dropzone" 
        [class.dragging]="isDragging"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (click)="fileInput.click()"
      >
        <input 
          #fileInput 
          type="file" 
          accept="image/*" 
          style="display: none" 
          (change)="onFileSelected($event)"
        />

        <div *ngIf="!currentImageUrl && !isUploading" class="dropzone-content">
          <span class="upload-icon">☁️</span>
          <p class="upload-text"><strong>Click to upload</strong> or drag & drop image</p>
          <span class="upload-hint">PNG, JPG, WEBP up to 5MB (Uploaded to Cloudinary)</span>
        </div>

        <div *ngIf="isUploading" class="uploading-state">
          <div class="spinner"></div>
          <p>Uploading image to Cloudinary...</p>
        </div>

        <div *ngIf="currentImageUrl && !isUploading" class="preview-container">
          <img [src]="currentImageUrl" alt="Product Image Preview" class="preview-img" />
          <div class="preview-overlay">
            <span class="badge badge-gold" *ngIf="uploadProvider">{{ uploadProvider }}</span>
            <button type="button" class="btn-danger" (click)="removeImage($event)">Remove Image</button>
          </div>
        </div>
      </div>

      <div *ngIf="errorMessage" class="error-alert">
        ⚠️ {{ errorMessage }}
      </div>
    </div>
  `,
  styles: [`
    .upload-container {
      width: 100%;
    }

    .dropzone {
      border: 2px dashed var(--border-color);
      border-radius: var(--radius-md);
      background: rgba(255, 255, 255, 0.02);
      padding: 24px;
      text-align: center;
      cursor: pointer;
      transition: all 0.25s ease;
      min-height: 180px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
    }

    .dropzone:hover, .dropzone.dragging {
      border-color: var(--accent-gold);
      background: var(--accent-gold-glow);
    }

    .dropzone-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    .upload-icon {
      font-size: 36px;
    }

    .upload-text {
      font-size: 14px;
      color: var(--text-primary);
    }

    .upload-hint {
      font-size: 11px;
      color: var(--text-muted);
    }

    .uploading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      color: var(--accent-gold);
      font-size: 13px;
    }

    .spinner {
      width: 28px;
      height: 28px;
      border: 3px solid rgba(201, 169, 110, 0.2);
      border-top-color: var(--accent-gold);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .preview-container {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .preview-img {
      max-height: 160px;
      object-fit: contain;
      border-radius: var(--radius-sm);
    }

    .preview-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    .preview-container:hover .preview-overlay {
      opacity: 1;
    }

    .error-alert {
      margin-top: 8px;
      font-size: 12px;
      color: var(--danger);
    }
  `]
})
export class ImageUploaderComponent {
  @Input() currentImageUrl: string = '';
  @Input() cloudinaryId: string = '';
  @Output() imageUploaded = new EventEmitter<{ url: string; cloudinaryId: string }>();
  @Output() imageRemoved = new EventEmitter<void>();

  isDragging = false;
  isUploading = false;
  uploadProvider = '';
  errorMessage = '';

  constructor(private uploadService: UploadService) {}

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  private handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Please select a valid image file.';
      return;
    }

    this.errorMessage = '';
    this.isUploading = true;

    this.uploadService.uploadImage(file).subscribe({
      next: (res: UploadResponse) => {
        this.isUploading = false;
        this.currentImageUrl = res.imageUrl;
        this.cloudinaryId = res.cloudinaryId;
        this.uploadProvider = res.provider === 'cloudinary' ? 'Cloudinary Secured' : 'Base64 Local';
        this.imageUploaded.emit({
          url: res.imageUrl,
          cloudinaryId: res.cloudinaryId
        });
      },
      error: (err) => {
        this.isUploading = false;
        this.errorMessage = err.error?.message || 'Upload failed. Please try again.';
      }
    });
  }

  removeImage(event: Event) {
    event.stopPropagation();
    this.currentImageUrl = '';
    this.cloudinaryId = '';
    this.imageRemoved.emit();
  }
}
